import Box from '@mui/material/Box';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { OptionsMap } from '@openx/types/options.ts';
import type { RetargetingFormState } from '@openx/types/targeting/retargeting.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import { getRetargetingListPresentation } from '../utils/getRetargetingListPresentation.ts';

type SegmentListDisplayProps = {
  segment: RetargetingFormState;
  readonly?: boolean;
  onClear: () => void;
  disabledReasonMessage?: string;
  isLoading?: boolean;
  options?: OptionsMap;
};

export const SegmentListDisplay = ({
  segment,
  readonly = true,
  onClear,
  disabledReasonMessage,
  isLoading = false,
  options,
}: SegmentListDisplayProps) => {
  const { t } = useTranslation();

  const handleClear = useCallback(() => {
    onClear();
  }, [onClear]);

  if (!segment.segment_id || !segment.disposition) {
    return null;
  }

  // The API persists only the segment id. The display name is resolved via the shared
  // options-by-path convention (options[id].name); while it is still being fetched,
  // show a loader instead of an empty row to avoid a blank flash on load.
  const hasResolvedName = Boolean(options?.[segment.segment_id]?.name);

  if (isLoading && !hasResolvedName) {
    return (
      <Box data-test="sub-section">
        <Loader />
      </Box>
    );
  }

  const { isInclude, sectionTitle, listTitle } = getRetargetingListPresentation(segment.disposition, t);

  return (
    <Box data-test="sub-section">
      <FiltersTitle
        onClear={handleClear}
        title={sectionTitle.toUpperCase()}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />
      <SelectedItems
        dataTest="retargeting-segment"
        selectedItems={[segment.segment_id]}
        isAddIcon={isInclude}
        filtersTitle={listTitle.toUpperCase()}
        readonly={readonly}
        disabledStyle={false}
        handleChange={handleClear}
        options={options}
      />
    </Box>
  );
};
